import { ChipsideComponent } from "../chipside-component";

export class Textarea extends ChipsideComponent {
	static observedAttributes = ["readonly"];

	#Mappers = {};

	static Observer = new ResizeObserver(entries => entries.forEach(entry => Textarea.#ResizeTextArea(entry)));

	constructor() {
		super();

		this.normalizeContent = false;
		this.identifier = this.getAttribute("identifier") ?? crypto.randomUUID().slice(0, 8);
		this.label = this.getAttribute("label");
		this.secondaryLabel = this.getAttribute("secondary-label") ?? "";
		this.helperText = this.getAttribute("helper-text");
		this.codePreview = this.hasAttribute("code-preview");
		this.responsive = this.hasAttribute("responsive");
		this.casing = this.getAttribute("casing") ?? "";
		this.readonly = this.hasAttribute("readonly");
		this.prefixButton = this.getAttribute("prefix-button") ?? "";
		this.suffixButton = this.getAttribute("suffix-button") ?? "";
		this.prefixTooltip = this.getAttribute("prefix-tooltip") ?? "";
		this.suffixTooltip = this.getAttribute("suffix-tooltip") ?? "";
		this.suffixIcon = this.getAttribute("suffix-icon") ?? "";
		this.prefixIcon = this.getAttribute("prefix-icon") ?? "";

		this.#Mappers = {
			maxlength: this.getAttribute("maxlength"),
			minlength: this.getAttribute("minlength"),
			name: this.getAttribute("name"),
			spellcheck: this.getAttribute("spellcheck"),
			placeholder: this.getAttribute("placeholder"),
			readonly: this.readonly,
			required: this.hasAttribute("required"),
			rows: this.getAttribute("rows"),
			autofocus: this.hasAttribute("autofocus")
		};

		if (!this.#Mappers.spellcheck && this.codePreview) { this.#Mappers.spellcheck = true; }
		if (this.closest("form") && !this.#Mappers.name) { this.#Mappers.name = this.id ?? this.identifier; }

		this.removeAttribute("autofocus");

		Object.keys(this.#Mappers).forEach(key => this[key] = this.#Mappers[key]);

		this.classList.add("textarea__wrapper");
	}

	get value() {
		return this.querySelector("textarea")?.value ?? this.textContent;
	}

	set value(val) {
		this.querySelector("textarea").value = val;
	}

	parsedCallback() {
		const element = Object.assign(document.createElement("div"), {
			innerHTML:
				`
				<label class="input-field__label">${this.label}<span class="input-field__label--secondary">${this.secondaryLabel}</span></label>
				<div class="input__wrapper">
					<textarea class="input-field input-field__textarea"></textarea>
				</div>
				<div class="input-field__helper-text">${this.helperText}</div>
				`.trim()
		}), textArea = element.querySelector("textarea");

		textArea.id = this.identifier;

		textArea.onchange = ev => {
			ev.preventDefault();
			ev.stopPropagation();

			this.reportValidity();
			this.closest("chip-form")?.checkValidity();

			this.raiseEvent("change");
		}

		if (!this.label && !this.secondaryLabel) { element.querySelector("label").remove(); }
		else { element.querySelector("label").htmlFor = this.identifier; }

		if (!this.secondaryLabel) { element.querySelector(".input-field__label--secondary")?.remove(); }
		if (!this.helperText) { element.querySelector(".input-field__helper-text").remove(); }
		if (this.casing) { textArea.classList.add(`${this.casing.toLowerCase()}-input`); }
		if (this.codePreview) { textArea.classList.add("code-preview"); }

		if (this.prefixButton) {
			textArea.insertAdjacentElement("beforebegin", document.createElementWithContents("chip-button", null, {
				className: "input__bound",
				buttonStyle: "icon",
				icon: this.prefixButton,
				variation: "body",
				onclick: () => this.raiseEvent("prefixclick")
			}));
		} else if (this.prefixIcon) {
			textArea.insertAdjacentElement("beforebegin", document.createElementWithContents("span", document.createElementWithContents("i", null, { className: this.prefixIcon }), { className: "input__bound" }));
		}

		if (this.suffixButton) {
			textArea.insertAdjacentElement("afterend", document.createElementWithContents("chip-button", null, {
				className: "input__bound",
				buttonStyle: "icon",
				icon: this.suffixButton,
				variation: "body",
				onclick: () => this.raiseEvent("suffixclick")
			}));
		} else if (this.suffixIcon) {
			textArea.insertAdjacentElement("afterend", document.createElementWithContents("span", document.createElementWithContents("i", null, { className: this.suffixIcon }), { className: "input__bound" }));
		}

		if (this.prefixTooltip) { this.querySelector(".input__bound:not(textArea+.input__bound)")?.setAttribute("tooltip", this.prefixTooltip); }
		if (this.suffixTooltip) { this.querySelector("textArea+.input__bound")?.setAttribute("tooltip", this.suffixTooltip); }

		if (this.responsive) {
			Textarea.Observer.observe(textArea);

			textArea.addEventListener("input", Textarea.#ResizeTextArea);
		}

		Object.keys(this.#Mappers).filter(x => this.#Mappers[x])
			.forEach(key => textArea.setAttribute(key, this.#Mappers[key] === true ? "" : this.#Mappers[key]));

		textArea.append(...this.childNodes);

		this.append(...element.childNodes);
	}

	focus() {
		this.querySelector("textarea")?.focus();
	}

	reportValidity() {
		const input = this.querySelector("textarea"),
			validity = input.checkValidity();

		this.querySelector(".input-field__validation-error")?.remove();
		this.querySelectorAll(".input-field__invalid").forEach(x => x.removeClass("input-field__invalid"));

		if (!validity) {
			this.appendChild(document.createElementWithContents("div", input.validationMessage, { className: "input-field__validation-error" }));
			input.classList.add("input-field__invalid");

			this.querySelectorAll(".input__bound").forEach(x => x.classList.add("input-field__invalid"));
		}

		return validity;
	}

	checkValidity() {
		return this.querySelector("textarea")?.checkValidity() ?? false;
	}

	attributeChanged(name, _, newValue) {
		if (name === "readonly") {
			this.querySelector("textarea").readOnly = this.IsAttributeValueTrue(newValue);
		}
	}

	static #ResizeTextArea(event) {
		const { target } = event;

		if (target) {
			const computed = window.getComputedStyle(target);

			let attempt = target;
			let scrollPreset = 0;

			while (scrollPreset === 0 && (attempt = attempt.parentElement)) {
				scrollPreset = attempt.scrollTop;
			}

			target.style.height = "0px";
			target.style.height = `${target.scrollHeight + parseFloat(computed.borderBottomWidth) + parseFloat(computed.borderTopWidth)}px`;
		}
	}
}